import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Sparkles, Send } from 'lucide-react';
import { CONTENT } from '@/constants/testIds';
import { toast } from 'sonner';

const CONTENT_TYPES = [
  'FAQ', 'Article', 'Authority Guide', 'Case Study', 'Landing Page',
  'Email Campaign', 'White Paper', 'LinkedIn Post', 'Facebook Post',
  'Instagram Post', 'Thread', 'YouTube Script', 'Podcast Script',
  'Press Release', 'Newsletter',
];

const PLATFORMS = ['WordPress', 'Webflow', 'LinkedIn', 'Facebook', 'Instagram', 'X', 'Medium', 'Substack', 'Mailchimp', 'HubSpot'];

export default function ContentFactory() {
  const { activeClient } = useClient();
  const [contentType, setContentType] = useState('FAQ');
  const [topic, setTopic] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!activeClient) return;
    const { data } = await api.get(`/clients/${activeClient.client_id}/content`);
    setItems(data || []);
  };

  useEffect(() => { load(); /* refresh on client change */ }, [activeClient]);

  if (!activeClient) return <div className="text-sm text-muted-foreground">Select a client.</div>;

  const generate = async () => {
    if (!topic.trim()) return toast.error('Add a topic');
    setLoading(true);
    try {
      await api.post('/content/generate', {
        client_id: activeClient.client_id, content_type: contentType, topic: topic.trim(),
      });
      setTopic('');
      await load();
      toast.success('Content generated');
    } catch (e) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, platform = null) => {
    await api.patch(`/content/${id}`, { status, platform });
    await load();
    toast.success(status === 'published' ? `Published to ${platform || 'platform'}` : `Marked ${status}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Content factory</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">Generate, edit, publish</h1>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="grid md:grid-cols-[200px_1fr_auto] gap-3">
          <Select value={contentType} onValueChange={setContentType}>
            <SelectTrigger data-testid={CONTENT.typeSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            data-testid={CONTENT.topicInput}
            placeholder="Topic — e.g. 'How AI changes B2B finance leadership'"
            value={topic}
            onChange={e => setTopic(e.target.value)}
          />
          <Button data-testid={CONTENT.generateBtn} onClick={generate} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            {loading ? 'Drafting…' : 'Generate'}
          </Button>
        </div>
      </div>

      <div data-testid={CONTENT.list} className="space-y-4">
        {items.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No content yet. Generate your first piece above.
          </div>
        )}
        {items.map(c => (
          <div key={c.content_id} className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">{c.content_type}</Badge>
                  <Badge variant={c.status === 'published' ? 'default' : 'secondary'}>{c.status}</Badge>
                  {c.platform && <span className="text-xs text-muted-foreground">→ {c.platform}</span>}
                </div>
                <h3 className="mt-2 font-display text-xl truncate">{c.result?.title || c.topic}</h3>
              </div>
              <div className="flex items-center gap-2">
                <Select onValueChange={(p) => updateStatus(c.content_id, 'published', p)}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Publish to…" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => updateStatus(c.content_id, 'scheduled')}>Schedule</Button>
              </div>
            </div>
            {c.result?.meta_description && (
              <div className="mt-3 text-xs text-muted-foreground italic">{c.result.meta_description}</div>
            )}
            {c.result?.body && (
              <pre className="mt-4 text-sm whitespace-pre-wrap font-sans text-foreground/90 leading-relaxed max-h-72 overflow-y-auto border-t border-border pt-4">{c.result.body}</pre>
            )}
            {(c.result?.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1">
                {c.result.tags.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}
              </div>
            )}
            {c.result?.cta && (
              <div className="mt-4 flex items-center gap-2 text-sm">
                <Send className="h-3 w-3 text-primary" />
                <span className="text-muted-foreground">CTA:</span> {c.result.cta}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
